import PropTypes from 'prop-types';

import { LdsHeaderProvider } from './LdsHeaderProvider.jsx';

import Header from './Header.jsx';

// Header sub components for export
import Divider from './TopNavMenuObjects/Divider.jsx';
import Dropdown from './TopNavMenuObjects/Dropdown.jsx';
import DropdownItem from './TopNavMenuObjects/DropdownItem.jsx';
import Link from './TopNavMenuObjects/Link.jsx';
import Flyout from './TopNavMenuObjects/Flyout.jsx';
import MegaMenu from './TopNavMenuObjects/MegaMenu.jsx';
import MegaMenuSection from './TopNavMenuObjects/MegaMenuSection.jsx';
import MegaMenuColumn from './TopNavMenuObjects/MegaMenuColumn.jsx';
import MegaMenuItem from './TopNavMenuObjects/MegaMenuItem.jsx';
import MegaMenuDivider from './TopNavMenuObjects/MeagMenuDivider.jsx';
import SearchSuggestion from './Search/SearchSuggestion.jsx';

// JSdoc type definitions
/**
 * @typedef LdsHeaderProps
 * @property {string} [menuAriaLabel] - The aria label for the menu
 * @property {React.ReactNode} [children] - The children of the component. Used to define custom content.
 * @property {boolean} [showUtilityLogo] - Whether to show the utility logo
 * @property {Array.<Object>} [utilityMenuLinks] - The utility menu links
 * @property {string} [utilityMenuAriaLabel] - The aria label for the utility menu
 * @property {React.ReactNode[]} [utilityMenuCustomLinkItems] - The custom utility menu link items
 * @property {string} [utilityMenuCustomLinkItemsPosition] - The position of the custom utility menu link items. Accepts: before, after
 * @property {React.JSX.Element} [contactLilly] - The contact lilly component
 * @property {React.JSX.Element} [languageSelector] - The language selector component
 * @property {import('../LdsLillyLogo/index.jsx').LdsLillyLogoProps} [lillyLogo] - The lilly logo configuration
 * @property {Object} [customLogo] - The custom logo configuration
 * @property {string} [labelMenu] - The label for the menu
 * @property {string} [labelMobileMenuOpened] - The label for the opened mobile menu
 * @property {string} [labelMobileMenuClosed] - The label for the closed mobile menu
 * @property {string} [labelCloseMenu] - The label for the close menu
 * @property {boolean} [sticky] - Whether the header should be sticky
 * @property {string} [className] - Additional classes to add to the component
 */
/**
 * LdsHeader component
 * @param {LdsHeaderProps & React.InputHTMLAttributes<HTMLInputElement>} props
 * @returns {React.JSX.Element}
 */
export default function LdsHeader({
  currentRoute = null,
  // Top Nav MENU
  topNavAriaLabel = 'Primary',
  children,
  // Utility MENU
  showUtilityLogo = true,
  utilityMenuLinks = [],
  utilityMenuAriaLabel = 'Global',
  utilityMenuCustomLinkItems,
  utilityMenuCustomLinkItemsPosition = 'after',
  contactLilly = null,
  languageSelector = null,
  // LOGO
  lillyLogo = {
    size: 'md',
    className: 'pd-0',
  },
  customLogo = undefined,
  // TEXT LABELS
  labelMenu = 'Menu',
  labelMobileMenuOpened = 'menu opened',
  labelMobileMenuClosed = 'menu closed',
  labelCloseMenu = 'Close',
  // SEARCH
  search = false,
  searchTerm,
  setSearchTerm,
  searchSuggestions,
  searchCustomResult,
  searchInputPlaceholder,
  searchBtnLabel,
  searchNoResultsText,
  searchAnotherSearchText,
  searchLoading,
  searchClearBtnAriaLabel,
  searchMobileBtnAriaLabel,
  searchMobileCloseAriaLabel,
  searchMobileCloseLabel,
  searchOnBtnClick,
  searchOnSuggestionClick,
  searchSubmitBtnAriaLabel,
  searchInputAriaDescription = 'Suggestions will update as you type',
  searchOpenedAnnouncement = 'Search open',
  searchClosedAnnouncement = 'Search closed',
  searchInputAriaLabel,
  // UTILITIES
  sticky = false,
  className = '',
  ...rest
}) {
  return (
    <LdsHeaderProvider
      currentRoute={currentRoute}
    >
      <Header
        sticky={sticky}
        topNavAriaLabel={topNavAriaLabel}
        className={className}
        contactLilly={contactLilly}
        customLogo={customLogo}
        labelCloseMenu={labelCloseMenu}
        labelMenu={labelMenu}
        labelMobileMenuClosed={labelMobileMenuClosed}
        labelMobileMenuOpened={labelMobileMenuOpened}
        languageSelector={languageSelector}
        lillyLogo={lillyLogo}
        // search
        search={search}
        searchAnotherSearchText={searchAnotherSearchText}
        searchBtnLabel={searchBtnLabel}
        searchClearBtnAriaLabel={searchClearBtnAriaLabel}
        searchCustomResult={searchCustomResult}
        searchInputPlaceholder={searchInputPlaceholder}
        searchLoading={searchLoading}
        searchMobileBtnAriaLabel={searchMobileBtnAriaLabel}
        searchNoResultsText={searchNoResultsText}
        searchSuggestions={searchSuggestions}
        searchTerm={searchTerm}
        searchOnBtnClick={searchOnBtnClick}
        searchSubmitBtnAriaLabel={searchSubmitBtnAriaLabel}
        searchInputAriaDescription={searchInputAriaDescription}
        setSearchTerm={setSearchTerm}
        searchMobileCloseAriaLabel={searchMobileCloseAriaLabel}
        searchMobileCloseLabel={searchMobileCloseLabel}
        searchOnSuggestionClick={searchOnSuggestionClick}
        // Utility menu
        showUtilityLogo={showUtilityLogo}
        utilityMenuAriaLabel={utilityMenuAriaLabel}
        utilityMenuCustomLinkItems={utilityMenuCustomLinkItems}
        utilityMenuCustomLinkItemsPosition={utilityMenuCustomLinkItemsPosition}
        utilityMenuLinks={utilityMenuLinks}
        searchOpenedAnnouncement={searchOpenedAnnouncement}
        searchClosedAnnouncement={searchClosedAnnouncement}
        searchInputAriaLabel={searchInputAriaLabel}
        {...rest}
      >
        {children}
      </Header>
    </LdsHeaderProvider>
  );
}

LdsHeader.propTypes = {
  menuAriaLabel: PropTypes.string,
  lillyLogo: PropTypes.object,
  customLogo: PropTypes.object,
  sticky: PropTypes.bool,
  labelMenu: PropTypes.string,
  labelMobileMenuOpened: PropTypes.string,
  labelMobileMenuClosed: PropTypes.string,
  labelCloseMenu: PropTypes.string,
  className: PropTypes.string,
  search: PropTypes.bool,
  showUtilityLogo: PropTypes.bool,
  utilityMenuLinks: PropTypes.array,
  utilityMenuAriaLabel: PropTypes.string,
  utilityMenuCustomLinkItems: PropTypes.array,
  utilityMenuCustomLinkItemsPosition: PropTypes.oneOf(['before', 'after']),
};

LdsHeader.Divider = Divider;
LdsHeader.Dropdown = Dropdown;
LdsHeader.DropdownItem = DropdownItem;
LdsHeader.Link = Link;
LdsHeader.Flyout = Flyout;

LdsHeader.MegaMenu = MegaMenu;
LdsHeader.MegaMenuSection = MegaMenuSection;
LdsHeader.MegaMenuColumn = MegaMenuColumn;
LdsHeader.MegaMenuItem = MegaMenuItem;
LdsHeader.MegaMenuDivider = MegaMenuDivider;

LdsHeader.SearchSuggestion = SearchSuggestion;
